import React, { useState, useEffect } from "react";
import { Button, Text, Progress, Group, Stack, Alert } from "@mantine/core";
import { IconDownload, IconRefresh, IconInfoCircle } from "@tabler/icons-react";

interface UpdateProgress {
  percent: number;
  transferred: number;
  total: number;
  message?: string;
}

const UpdateChecker: React.FC = () => {
  const [currentVersion, setCurrentVersion] = useState<string>("");
  const [isChecking, setIsChecking] = useState(false);
  const [updateProgress, setUpdateProgress] = useState<UpdateProgress | null>(
    null,
  );
  const [message, setMessage] = useState<string>("");
  const [updateSupported, setUpdateSupported] = useState<boolean>(true);
  const [unsupportedReason, setUnsupportedReason] = useState<string>("");

  useEffect(() => {
    // Get current app version
    if (window.api?.getAppVersion) {
      window.api.getAppVersion((version: string) => {
        setCurrentVersion(version);
      });
    }

    // Check if auto-updates are supported
    if (window.api?.checkUpdateSupport) {
      window.api.checkUpdateSupport(
        (support: { supported: boolean; reason: string | null }) => {
          setUpdateSupported(support.supported);
          if (!support.supported && support.reason) {
            setUnsupportedReason(support.reason);
            setMessage(support.reason);
          }
        },
      );
    }

    // Set up update progress listener
    if (window.api?.onUpdateProgress) {
      window.api.onUpdateProgress((progress: UpdateProgress) => {
        setUpdateProgress(progress);
        setMessage(
          progress.message || `Downloading update: ${progress.percent}%`,
        );
      });
    }

    return () => {
      // Cleanup if needed
    };
  }, []);

  const handleCheckForUpdates = () => {
    if (!updateSupported) {
      setMessage(unsupportedReason || "Auto-updates are not supported");
      return;
    }

    if (!window.api?.checkForUpdates) {
      setMessage("Update functionality not available");
      return;
    }

    setIsChecking(true);
    setMessage("Checking for updates...");

    window.api.checkForUpdates();

    // Reset checking state after a reasonable time
    setTimeout(() => {
      setIsChecking(false);
      if (message === "Checking for updates...") {
        setMessage("Update check completed");
      }
    }, 5000);
  };

  const handleDownloadUpdate = () => {
    if (!window.api?.downloadUpdate) {
      setMessage("Download functionality not available");
      return;
    }

    setMessage("Starting download...");
    window.api.downloadUpdate();
  };

  const handleInstallUpdate = () => {
    if (!window.api?.quitAndInstall) {
      setMessage("Install functionality not available");
      return;
    }

    window.api.quitAndInstall();
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <Stack
      gap="md"
      style={{ maxWidth: 400, margin: "0 auto", padding: "1rem" }}>
      <Alert
        icon={<IconInfoCircle size="1rem" />}
        title="App Version"
        color="blue">
        <Text size="sm">Current version: {currentVersion || "Unknown"}</Text>
      </Alert>

      <Group grow>
        <Button
          leftSection={<IconRefresh size="1rem" />}
          onClick={handleCheckForUpdates}
          loading={isChecking}
          variant="outline"
          disabled={!updateSupported}>
          Check for Updates
        </Button>

        <Button
          leftSection={<IconDownload size="1rem" />}
          onClick={handleDownloadUpdate}
          disabled={
            !updateSupported ||
            (!updateProgress && !message.includes("available"))
          }
          variant="filled">
          Download Update
        </Button>
      </Group>

      {updateProgress && (
        <Stack gap="xs">
          <Progress
            value={updateProgress.percent}
            label={`${updateProgress.percent}%`}
            size="lg"
            radius="md"
          />
          <Text size="xs" c="dimmed" ta="center">
            {formatBytes(updateProgress.transferred)} /{" "}
            {formatBytes(updateProgress.total)}
          </Text>
        </Stack>
      )}

      {message && (
        <Alert
          color={
            message.includes("error")
              ? "red"
              : !updateSupported
                ? "yellow"
                : "blue"
          }>
          <Text size="sm">{message}</Text>
          {!updateSupported && (
            <Text size="xs" c="dimmed" mt="xs">
              Visit the GitHub releases page for manual updates.
            </Text>
          )}
        </Alert>
      )}

      {message.includes("ready") && (
        <Button onClick={handleInstallUpdate} color="green" fullWidth>
          Install and Restart
        </Button>
      )}
    </Stack>
  );
};

export default UpdateChecker;
